<template>
  <div>
    <v-container fluid>
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-4">
        <div>
          <h1 class="text-h4 font-weight-bold mb-1">考勤管理</h1>
        </div>
        <div class="d-flex gap-2">
          <v-btn
            v-if="canEditRole"
            color="primary"
            class="font-weight-bold text-capitalize bg-gradient-primary"
            @click="showCreateLeaveDialog = true"
          >
            <v-icon left>mdi-plus</v-icon>
            新增假期
          </v-btn>
          <v-btn
            v-if="canEditRole"
            color="warning"
            class="font-weight-bold text-capitalize bg-gradient-warning ml-2"
            @click="showTyphoonLeaveDialog = true"
          >
            <v-icon left>mdi-weather-hurricane</v-icon>
            新增天災停班
          </v-btn>
        </div>
      </div>

      <!-- Tabs -->
      <div class="bg-white rounded-lg overflow-hidden">
        <v-tabs
          v-model="activeTab"
          background-color="grey lighten-4"
          class="elevation-0"
          color="#cb0c9f"
        >
          <v-tab class="px-6">
            <v-icon size="20" class="mr-2">mdi-file-document-outline</v-icon
            >所有申請單
          </v-tab>
          <v-tab class="px-6">
            <v-icon size="20" class="mr-2" color="orange"
              >mdi-clock-check-outline</v-icon
            >
            待審核
          </v-tab>
          <v-tab class="px-6">
            <v-icon size="20" class="mr-2" color="blue"
              >mdi-calendar-check</v-icon
            >
            請假紀錄
          </v-tab>
          <v-tab class="px-6" v-if="canEditRole">
            <v-icon size="20" class="mr-2" color="orange"
              >mdi-clock-outline</v-icon
            >
            加班
          </v-tab>
          <v-tab class="px-6" v-if="canEditRole">
            <v-icon size="20" class="mr-2" color="green">mdi-calendar</v-icon>
            特休假
          </v-tab>
          <v-tab class="px-6" v-if="false">
            <v-icon size="20" class="mr-2" color="red">mdi-calendar</v-icon>
            銷假
          </v-tab>
          <v-tab class="px-6">
            <v-icon size="20" class="mr-2" color="purple"
              >mdi-account-clock-outline</v-icon
            >
            請假期間打卡
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="bg-white">
          <!-- Leave Requests Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text class="pa-0">
                <!-- Filter Section for View Mode -->
                <div class="px-4 pt-4">
                  <v-row>
                    <v-col cols="12" md="2">
                      <v-menu
                        v-model="startDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="filters.start_date"
                            label="開始日期"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            hide-details
                            clearable
                            @click:clear="filters.start_date = ''"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filters.start_date"
                          @input="startDateMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-menu
                        v-model="endDateMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="filters.end_date"
                            label="結束日期"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            hide-details
                            clearable
                            @click:clear="filters.end_date = ''"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filters.end_date"
                          @input="endDateMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-select
                        v-model="filters.apply_type"
                        :items="applyTypeOptions"
                        label="申請類型"
                        hide-details
                        clearable
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="2" v-if="filters.apply_type === 1">
                      <v-select
                        v-model="filters.leave_type"
                        :items="leaveTypeOptions"
                        label="假別"
                        hide-details
                        clearable
                        multiple
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-select
                        v-model="filters.review_status"
                        :items="reviewStatusOptions"
                        label="審核狀態"
                        hide-details
                        clearable
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-autocomplete
                        v-model="filters.user_id"
                        :items="userOptions"
                        :loading="userLoading"
                        :search-input.sync="userSearch"
                        item-text="display"
                        item-value="id"
                        label="選擇員工"
                        hide-details
                        clearable
                      >
                        <template v-slot:item="{ item }">
                          <div class="d-flex flex-column">
                            <span>{{ item.name }}</span>
                            <span class="text-caption grey--text">{{
                              item.email
                            }}</span>
                          </div>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col
                      cols="12"
                      md="auto"
                      class="d-flex align-center flex-wrap ml-auto justify-end"
                    >
                      <v-btn
                        @click="applyFilters"
                        color="primary"
                        class="
                          font-weight-bold
                          text-capitalize
                          bg-gradient-primary
                        "
                        depressed
                      >
                        <v-icon left>mdi-magnify</v-icon>
                        搜尋
                      </v-btn>
                      <v-btn
                        @click="resetFilters"
                        color="secondary"
                        class="
                          font-weight-bold
                          text-capitalize
                          bg-gradient-default
                          ml-2
                        "
                        depressed
                      >
                        <v-icon left>mdi-refresh</v-icon>
                        重置
                      </v-btn>
                      <v-btn
                        v-if="filters.apply_type === 1"
                        @click="exportLeaveApplications"
                        :loading="exportingExcel"
                        color="success"
                        class="
                          font-weight-bold
                          text-capitalize
                          bg-gradient-success
                          ml-2
                        "
                        depressed
                      >
                        <v-icon left>mdi-file-excel</v-icon>
                        匯出
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>

                <!-- Leave Request Cards -->
                <div class="leave-requests-container py-3">
                  <application-request-list
                    :requests="leaveRequests"
                    :loading="loading"
                    empty-title="目前沒有表單"
                    mode="view"
                    :can-edit="canEditRole && false"
                    :has-more="page < totalPages"
                    :loading-more="loadingMore"
                    @load-more="onLoadMore"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Pending Approvals Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text class="pa-0">
                <div class="leave-requests-container py-3">
                  <application-request-list
                    :requests="pendingApprovals"
                    :loading="loadingApprovals"
                    empty-title="沒有待審核的表單"
                    empty-text="沒有表單需要審核"
                    empty-icon="mdi-check-circle-outline"
                    mode="review"
                    :has-more="pendingPage < pendingTotalPages"
                    :loading-more="loadingMore"
                    @load-more="onLoadMore"
                    @remove="handleRemoveRequest"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Employee Leave Tab (Nested) -->
          <v-tab-item>
            <div class="px-4 py-2">
              <v-tabs
                v-model="employeeLeaveTab"
                background-color="transparent"
                class="mb-4"
                color="blue"
              >
                <v-tab>可用假期總覽</v-tab>
                <v-tab>員工請假搜尋</v-tab>
                <v-tab>員工請假紀錄</v-tab>
                <!-- 新增或審核請假紀錄 -->
                <v-tab v-if="canEditRole">匯入請假紀錄</v-tab>
              </v-tabs>

              <v-tabs-items v-model="employeeLeaveTab" class="bg-transparent">
                <v-tab-item>
                  <leave-balances-widget
                    ref="leaveBalancesWidget"
                    :selected-company="selectedCompany"
                    @error="handleWidgetError"
                  />
                </v-tab-item>
                <v-tab-item>
                  <employee-leave-history-widget
                    ref="employeeLeaveHistoryWidget"
                    :selected-company="selectedCompany"
                  />
                </v-tab-item>
                <v-tab-item>
                  <user-leave-history-widget
                    ref="userLeaveHistoryWidget"
                    :selected-company="selectedCompany"
                  />
                </v-tab-item>
                <v-tab-item>
                  <import-leave-balances-widget
                    ref="importLeaveBalancesWidget"
                    :selected-company="selectedCompany"
                    @error="handleWidgetError"
                    @success="handleImportSuccess"
                  />
                </v-tab-item>
              </v-tabs-items>
            </div>
          </v-tab-item>

          <!-- Overtime Tab -->
          <v-tab-item>
            <overtime-record-tab
              ref="overtimeRecordTab"
              :selected-company="selectedCompany"
              @error="handleWidgetError"
            />
          </v-tab-item>
          <v-tab-item>
            <annual-leave-record-tab
              ref="annualLeaveRecordTab"
              :selected-company="selectedCompany"
              @error="handleWidgetError"
            />
          </v-tab-item>
          <!-- Consumption Tab -->
          <v-tab-item v-if="false">
            <attachment-required-widget
              ref="attachmentRequiredWidget"
              :selected-company="selectedCompany"
              :can-edit="canEditRole"
              @error="handleWidgetError"
            />
          </v-tab-item>
          <v-tab-item>
            <leave-re-clock-widget
              ref="leaveReClockWidget"
              :selected-company="selectedCompany"
              @error="handleWidgetError"
            />
          </v-tab-item>
          <!-- 考勤紀錄表 -->
          <!-- 考勤紀錄表 -->
          <v-tab-item v-if="canEditRole">
            <salary-preview
              ref="slaryPrview"
              :allowed-export-types="['attendance']"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>

      <!-- Info Dialog for error messages -->
      <info-dialog
        :visible.sync="showInfoDialog"
        :title="infoDialogTitle"
        :message="infoDialogMessage"
        yes-button-text="確定"
      />

      <!-- Create Leave Application Dialog -->
      <create-leave-application-dialog
        :visible.sync="showCreateLeaveDialog"
        @success="handleCreateSuccess"
        @error="handleCreateError"
      />

      <!-- Create Typhoon Leave Dialog -->
      <create-typhoon-leave-dialog
        :visible.sync="showTyphoonLeaveDialog"
        :company-id="company_id"
        @success="handleTyphoonLeaveSuccess"
        @error="handleTyphoonLeaveError"
      />
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SalaryPreview from "@/views/Pages/Salary/SalaryPreview.vue";
import FormattedDate from "@/components/FormattedDate.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import OvertimeRecordTab from "@/components/Widgets/OvertimeRecordTab.vue";
import AnnualLeaveRecordTab from "@/components/Widgets/AnnualLeaveRecordTab.vue";
import LeaveBalancesWidget from "@/components/Widgets/LeaveBalancesWidget.vue";
import UserLeaveHistoryWidget from "@/components/Widgets/UserLeaveHistoryWidget.vue";
import EmployeeLeaveHistoryWidget from "@/components/Widgets/EmployeeLeaveHistoryWidget.vue";
import AttachmentRequiredWidget from "@/components/Widgets/AttachmentRequiredWidget.vue";
import LeaveReClockWidget from "@/components/Widgets/LeaveReClockWidget.vue";
import ImportLeaveBalancesWidget from "@/components/Widgets/ImportLeaveBalancesWidget.vue";
import CreateLeaveApplicationDialog from "@/components/Dialogs/CreateLeaveApplicationDialog.vue";
import CreateTyphoonLeaveDialog from "@/components/Dialogs/CreateTyphoonLeaveDialog.vue";
import ApplicationRequestList from "@/components/Widgets/ApplicationRequestList.vue";
// import LEAVE_TYPE from constants
import {
  REVIEW_STATUS,
  REVIEW_STATUS_TEXT,
  REVIEW_STATUS_COLOR,
  LEAVE_TYPE,
  APPLY_TYPE,
} from "@/constants";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";

export default {
  name: "LeavingManagement",
  components: {
    FormattedDate,
    InfoDialog,
    OvertimeRecordTab,
    AnnualLeaveRecordTab,
    LeaveBalancesWidget,
    UserLeaveHistoryWidget,
    EmployeeLeaveHistoryWidget,
    AttachmentRequiredWidget,
    LeaveReClockWidget,
    ImportLeaveBalancesWidget,
    CreateLeaveApplicationDialog,
    CreateTyphoonLeaveDialog,
    ApplicationRequestList,
    SalaryPreview,
  },
  data() {
    return {
      REVIEW_STATUS,
      REVIEW_STATUS_TEXT,
      REVIEW_STATUS_COLOR,
      // Confirmation dialog

      // Info dialog
      showInfoDialog: false,
      infoDialogTitle: "",
      infoDialogMessage: "",
      activeTab: 0,
      employeeLeaveTab: 0,
      showLeaveRequestDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      formValid: false,
      loading: false,
      loadingApprovals: false,
      submitting: false,
      leaveRequests: [],
      pendingApprovals: [],

      // company clock setting (used for working hours and lunch break)
      companyClockSetting: null,

      // Create leave dialog
      showCreateLeaveDialog: false,

      // Typhoon leave dialog
      showTyphoonLeaveDialog: false,
      page: 1,
      totalPages: 0,
      pendingPage: 1,
      pendingTotalPages: 0,
      loadingMore: false,
      filters: {
        start_date: "",
        end_date: "",
        apply_type: null,
        leave_type: [],
        review_status: null,
        user_id: null,
      },
      userOptions: [],
      userLoading: false,
      userSearch: "",
      userSearchTimer: null,
      exportingExcel: false,
    };
  },
  computed: {
    ...mapGetters("company", ["companies", "selectedCompany"]),
    ...mapGetters("user", ["canEditRole", "rootRole"]),
    company_id() {
      return this.selectedCompany ? this.selectedCompany.id : 1;
    },
    applyTypeOptions() {
      return Object.entries(APPLY_TYPE).map(([value, text]) => ({
        text,
        value: Number(value),
      }));
    },
    leaveTypeOptions() {
      return Object.entries(LEAVE_TYPE).map(([value, text]) => ({
        text,
        value: parseInt(value),
      }));
    },
    reviewStatusOptions() {
      return [
        { text: "未審核", value: 0 },
        { text: "審核中", value: 1 },
        { text: "已通過", value: 2 },
        { text: "已拒絕", value: 3 },
        { text: "已撤回", value: 4 },
        { text: "異常", value: 5 },
        { text: "同意代理", value: 6 },
        { text: "已撤銷", value: 7 },
      ];
    },
  },
  watch: {
    $route(to, from) {
      let activeTabChanged = false;
      if (to.query.tab !== undefined) {
        const targetTab = Number(to.query.tab);
        if (this.activeTab !== targetTab) {
          this.activeTab = targetTab;
          activeTabChanged = true;
        }
      }

      if (activeTabChanged) {
        return;
      }

      if (
        to.query.application_id &&
        to.query.application_id !== from.query.application_id
      ) {
        this.$nextTick(() => {
          const el = document.getElementById(
            `application-request-${to.query.application_id}`
          );
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          } else {
            if (this.activeTab === 0) {
              this.fetchLeaveRequests();
            } else if (this.activeTab === 1) {
              this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
            }
          }
        });
      }
    },
    // watch company_id changes
    company_id() {
      if (this.activeTab === 0) {
        // 所有申請單
        this.fetchLeaveRequests();
      } else if (this.activeTab === 1) {
        // 待審核
        this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
      } else if (this.activeTab === 2) {
        if (this.employeeLeaveTab === 0) {
          this.$refs.leaveBalancesWidget?.fetchLeaveBalances();
        } else if (this.employeeLeaveTab === 1) {
          this.$refs.employeeLeaveHistoryWidget?.refresh();
        } else {
          this.$refs.userLeaveHistoryWidget?.refresh();
        }
      } else if (this.activeTab === 3) {
        this.$refs.overtimeRecordTab?.refresh();
      } else if (this.activeTab === 4) {
        this.$refs.annualLeaveRecordTab?.refresh();
      } else if (this.activeTab === 5) {
        this.$refs.leaveReClockWidget?.fetchLeaveReClock();
      } else if (this.activeTab === 6) {
        this.$refs.slaryPrview?.fetchSalaries();
      }
    },
    "filters.apply_type"(val) {
      if (val !== 1) {
        this.filters.leave_type = [];
      }
    },
    userSearch(val) {
      if (this.userSearchTimer) clearTimeout(this.userSearchTimer);
      this.userSearchTimer = setTimeout(() => {
        this.fetchUserOptions(val);
      }, 300);
    },
    employeeLeaveTab(newVal) {
      if (this.activeTab === 2) {
        this.$nextTick(() => {
          if (newVal === 0) {
            this.$refs.leaveBalancesWidget?.fetchLeaveBalances();
          } else if (newVal === 1) {
            this.$refs.employeeLeaveHistoryWidget?.refresh();
          } else if (newVal === 2) {
            this.$refs.userLeaveHistoryWidget?.refresh();
          } else if (newVal === 3) {
            this.$refs.importLeaveBalancesWidget?.fetchDrafts();
          }
        });
      }
    },
    // when switching tabs, trigger appropriate fetch
    activeTab(newVal) {
      console.log("active tab ", newVal);
      if (newVal === 0) {
        // 所有申請單
        this.fetchLeaveRequests();
      } else if (newVal === 1) {
        // 待審核
        this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
      } else if (newVal === 2) {
        this.$nextTick(() => {
          if (this.employeeLeaveTab === 0) {
            this.$refs.leaveBalancesWidget?.fetchLeaveBalances();
          } else if (this.employeeLeaveTab === 1) {
            this.$refs.employeeLeaveHistoryWidget?.refresh();
          } else if (this.employeeLeaveTab === 2) {
            this.$refs.userLeaveHistoryWidget?.refresh();
          }
        });
      } else if (newVal === 3) {
        this.$nextTick(() => {
          this.$refs.overtimeRecordTab?.refresh();
        });
      } else if (newVal === 4) {
        this.$nextTick(() => {
          this.$refs.annualLeaveRecordTab?.refresh();
        });
      } else if (newVal === 5) {
        this.$nextTick(() => {
          this.$refs.leaveReClockWidget?.fetchLeaveReClock();
        });
      } else if (newVal === 6) {
        console.loo("leaveReClockWidget fetchLeaveReClock");
        this.$nextTick(() => {
          this.$refs.slaryPrview?.fetchSalaries();
        });
      }
    },
  },
  created() {
    if (this.$route.query.tab !== undefined) {
      this.activeTab = Number(this.$route.query.tab);
    }

    if (this.activeTab === 0) {
      this.fetchLeaveRequests();
    } else if (this.activeTab === 1) {
      this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
    } else if (this.activeTab === 5) {
      this.$nextTick(() => {
        this.$refs.attachmentRequiredWidget?.fetchAttachmentRequired();
      });
    } else if (this.activeTab === 6) {
      this.$nextTick(() => {
        this.$refs.leaveReClockWidget?.fetchLeaveReClock();
      });
    }

    // Leave balances tab will load via widget when active
    // Load company clock settings for computing working minutes
    this.fetchCompanyClockSettings();
    // Annual leave tab will load via widget when active
  },
  methods: {
    // Format date using date-fns (legacy method, use FormattedDate component instead)
    formatDate(date) {
      if (!date) return "";
      try {
        // This method is kept for backward compatibility
        // Prefer using the FormattedDate component in templates
        return date;
      } catch (error) {
        console.error("Error formatting date:", error);
        return date;
      }
    },

    async exportLeaveApplications() {
      try {
        this.exportingExcel = true;
        const currentCompanyID = this.selectedCompany
          ? this.selectedCompany.id
          : 1;

        const params = new URLSearchParams({
          company_id: String(currentCompanyID),
        });

        if (this.filters.start_date) {
          params.append("start_date", this.filters.start_date);
        }
        if (this.filters.end_date) {
          params.append("end_date", this.filters.end_date);
        }
        if (
          this.filters.leave_type &&
          (Array.isArray(this.filters.leave_type)
            ? this.filters.leave_type.length > 0
            : Boolean(this.filters.leave_type))
        ) {
          params.append(
            "leave_type",
            Array.isArray(this.filters.leave_type)
              ? this.filters.leave_type.join(",")
              : this.filters.leave_type
          );
        }
        if (
          this.filters.review_status !== null &&
          this.filters.review_status !== undefined &&
          this.filters.review_status !== ""
        ) {
          params.append("review_status", this.filters.review_status);
        }
        if (this.filters.user_id) {
          params.append("user_id", this.filters.user_id);
        }

        const response = await this.$axios.get(
          `/api/applications/export?${params.toString()}`,
          { responseType: "blob" }
        );

        let filename = `請假申請紀錄_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        const disposition = response.headers?.["content-disposition"];
        if (disposition) {
          const matchUtf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
          if (matchUtf8 && matchUtf8[1]) {
            try {
              filename = decodeURIComponent(matchUtf8[1]);
            } catch (e) {
              filename = matchUtf8[1];
            }
          } else {
            const match = disposition.match(/filename="?([^";]+)"?/i);
            if (match && match[1]) {
              try {
                filename = decodeURIComponent(match[1]);
              } catch (e) {
                filename = match[1];
              }
            }
          }
        }

        const blob = new Blob([response.data], {
          type:
            response.headers?.["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting leave applications:", error);
        let errorMsg = "匯出失敗，請稍後再試";
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            if (json.message) errorMsg = json.message;
          } catch (e) {
            // ignore
          }
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        }
        this.infoDialogTitle = "匯出失敗";
        this.infoDialogMessage = errorMsg;
        this.showInfoDialog = true;
      } finally {
        this.exportingExcel = false;
      }
    },

    applyFilters() {
      if (this.activeTab === 0) {
        this.fetchLeaveRequests();
      }
    },
    resetFilters() {
      this.filters = {
        start_date: "",
        end_date: "",
        apply_type: null,
        leave_type: [],
        review_status: null,
        user_id: null,
      };
      if (this.activeTab === 0) {
        this.fetchLeaveRequests();
      }
    },
    async fetchUserOptions(searchTerm = "") {
      if (!this.company_id) {
        this.userOptions = [];
        return;
      }
      this.userLoading = true;
      try {
        const params = new URLSearchParams({
          is_active: "1",
          approval_status: "審核通過",
          company_id: String(this.company_id),
        });
        if (searchTerm && searchTerm.trim()) {
          params.append("name", searchTerm.trim());
        }
        const response = await this.$axios.get(
          `/api/users/all?${params.toString()}`
        );
        const data = response?.data?.data || [];
        this.userOptions = data.map((user) => ({
          id: user.id,
          name: user.name || `使用者 #${user.id}`,
          email: user.email || "",
          display: user.name || `使用者 #${user.id}`,
        }));
      } catch (error) {
        console.error("Error fetching user options:", error);
        this.userOptions = [];
      } finally {
        this.userLoading = false;
      }
    },

    handleWidgetError({ title, message }) {
      console.log("handleWidgetError", title, message);
      if (message == "Network Error") {
        message = "請檢查網路連線";
      }
      this.infoDialogTitle = title;
      this.infoDialogMessage = message;
      this.showInfoDialog = true;
    },
    async fetchLeaveRequests(review_status = null, append = false) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }

      // Get the current user ID from auth context (or use a placeholder for now)
      const currentCompanyID = this.selectedCompany
        ? this.selectedCompany.id
        : 1;

      // Determine page based on status/tab and append mode
      let currentPage = 1;
      if (review_status == REVIEW_STATUS.UNREVIEWED) {
        currentPage = append ? this.pendingPage + 1 : 1;
      } else {
        currentPage = append ? this.page + 1 : 1;
      }

      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 15,
        company_id: currentCompanyID,
      });
      //     review_status: '審核狀態 0:未審核 1:審核中 2. 已通過 3. 已拒絕 4. 已撤回 5.異常'
      if (review_status !== null && review_status !== undefined) {
        queryParams.append("review_status", review_status);
      } else {
        // Apply filters for "所有申請單" tab (where filter UI is displayed)
        if (this.filters.start_date)
          queryParams.append("start_date", this.filters.start_date);
        if (this.filters.end_date)
          queryParams.append("end_date", this.filters.end_date);
        if (
          this.filters.apply_type !== null &&
          this.filters.apply_type !== undefined
        ) {
          queryParams.append("apply_type", this.filters.apply_type);
        }
        if (
          this.filters.apply_type === 1 &&
          this.filters.leave_type &&
          (Array.isArray(this.filters.leave_type)
            ? this.filters.leave_type.length > 0
            : Boolean(this.filters.leave_type))
        ) {
          queryParams.append(
            "leave_type",
            Array.isArray(this.filters.leave_type)
              ? this.filters.leave_type.join(",")
              : this.filters.leave_type
          );
        }
        if (
          this.filters.review_status !== null &&
          this.filters.review_status !== undefined &&
          this.filters.review_status !== ""
        ) {
          queryParams.append("review_status", this.filters.review_status);
        }
        if (this.filters.user_id)
          queryParams.append("user_id", this.filters.user_id);
      }

      // if review_status == REVIEW_STATUS.UNREVIEWED, add review_mode=1
      if (review_status == REVIEW_STATUS.UNREVIEWED) {
        queryParams.append("review_mode", 1);
      }

      try {
        const response = await this.$axios.get(
          `/api/applications?${queryParams}`
        );

        const newData = response.data.data || [];
        const pagination = response.data.pagination || {};

        if (review_status == REVIEW_STATUS.UNREVIEWED) {
          if (append) {
            this.pendingApprovals = [...this.pendingApprovals, ...newData];
            this.pendingPage = currentPage;
          } else {
            this.pendingApprovals = newData;
            this.pendingPage = 1;
          }
          this.pendingTotalPages = pagination.totalPages || 0;

          // Auto-scroll to specific application if application_id is provided in query
          const appId = this.$route.query.application_id;
          if (appId) {
            this.$nextTick(() => {
              const el = document.getElementById(
                `application-request-${appId}`
              );
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            });
          }
        } else {
          if (append) {
            this.leaveRequests = [...this.leaveRequests, ...newData];
            this.page = currentPage;
          } else {
            this.leaveRequests = newData;
            this.page = 1;
          }
          this.totalPages = pagination.totalPages || 0;
        }
      } catch (error) {
        console.error("Error fetching leave requests:", error);
        alert("無法取得請假資料");
      } finally {
        this.loading = false;
        this.loadingMore = false;
        this.loadingApprovals = false;
      }
    },
    onLoadMore() {
      if (this.activeTab === 0) {
        this.fetchLeaveRequests(null, true);
      } else if (this.activeTab === 1) {
        this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED, true);
      }
    },

    // (Removed) fetchPendingApprovals previously used leaveService

    // Fetch company clock settings (for working hours/lunch)
    async fetchCompanyClockSettings() {
      try {
        const currentCompanyID = this.selectedCompany
          ? this.selectedCompany.id
          : 1;
        const response = await this.$axios.get(
          `/api/company-clock-settings/${currentCompanyID}`
        );
        const settings = Array.isArray(response.data) ? response.data : [];
        // Pick the first setting as active for now
        this.companyClockSetting = settings.length > 0 ? settings[0] : null;
      } catch (e) {
        console.error("Error fetching company clock settings:", e);
        this.companyClockSetting = null;
      }
    },

    // ===== Working time helpers =====
    parseTimeToMinutes(hms) {
      if (!hms) return null;
      const parts = String(hms).split(":");
      const h = parseInt(parts[0] || "0", 10);
      const m = parseInt(parts[1] || "0", 10);
      return h * 60 + m;
    },
    toMinutes(h, m) {
      return h * 60 + m;
    },
    clamp(val, min, max) {
      return Math.min(Math.max(val, min), max);
    },
    calculateDaySpan(startIso, endIso) {
      const start = new Date(startIso);
      const end = new Date(endIso);
      const startDate = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
      );
      const endDate = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate()
      );
      const diffMs = endDate - startDate;
      if (diffMs < 0) return 0;
      return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1; // inclusive days
    },
    calculateLunchBreakOverlap(startMin, endMin) {
      const s = this.companyClockSetting;
      if (!s || !s.lunch_break) return 0;
      const lbStart = this.parseTimeToMinutes(s.lunch_break_start);
      const lbEnd = this.parseTimeToMinutes(s.lunch_break_end);
      if (lbStart == null || lbEnd == null || lbEnd <= lbStart) return 0;
      const overlapStart = Math.max(startMin, lbStart);
      const overlapEnd = Math.min(endMin, lbEnd);
      return Math.max(0, overlapEnd - overlapStart);
    },
    // Format minutes to "X天Y小時". 8 working hours compose a single day.
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    // Show confirmation dialog for approve action
    handleRemoveRequest(id) {
      const index = this.pendingApprovals.findIndex((item) => item.id === id);
      if (index !== -1) {
        this.pendingApprovals.splice(index, 1);
      }
    },
    handleCreateSuccess() {
      this.infoDialogTitle = "新增成功";
      this.infoDialogMessage = "假期申請已成功新增";
      this.showInfoDialog = true;

      if (this.activeTab === 0) {
        this.fetchLeaveRequests();
      } else if (this.activeTab === 1) {
        this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
      }
    },
    handleCreateError({ title, message }) {
      this.infoDialogTitle = title;
      this.infoDialogMessage = message;
      this.showInfoDialog = true;
    },
    handleTyphoonLeaveSuccess() {
      this.infoDialogTitle = "新增成功";
      this.infoDialogMessage = "颱風假已成功為全公司員工新增";
      this.showInfoDialog = true;

      if (this.activeTab === 0) {
        this.fetchLeaveRequests();
      } else if (this.activeTab === 1) {
        this.fetchLeaveRequests(REVIEW_STATUS.UNREVIEWED);
      }
    },
    handleTyphoonLeaveError({ title, message }) {
      this.infoDialogTitle = title;
      this.infoDialogMessage = message;
      this.showInfoDialog = true;
    },
    handleImportSuccess(result) {
      this.infoDialogTitle = "匯入成功";
      this.infoDialogMessage = result.details || "假期餘額已成功匯入";
      this.showInfoDialog = true;
      // Refresh the balances widget if it exists
      this.$refs.leaveBalancesWidget?.fetchLeaveBalances();
    },
  },
};
</script>

<style scoped>
.v-tab--active {
  font-weight: bold !important;
  font-size: 1.05rem;
}
</style>
